import React, { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  NumberInput,
  Stack,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import { IconAlertCircle } from '@tabler/icons-react';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import Drawer from 'components/Drawer';
import FlowTriggerSelect from 'components/form-fields/select-fields/FlowTriggerSelect';
import { useAuth } from 'context/auth-context';
import useFlowDynamicTemplateVariables from 'hooks/flows/useFlowDynamicTemplateVariables';
import useGym from 'hooks/gym/useGym';
import useCreateNotificationMessageTemplate from 'hooks/notificationMessageTemplate/useCreateNotificationTemplateMessage';
import useNotificationMessageTemplate from 'hooks/notificationMessageTemplate/useNotificationTemplateMessage';
import useUpdateNotificationMessageTemplate from 'hooks/notificationMessageTemplate/useUpdateNotificationTemplateMessage';
import { Flow } from 'types';
import notificationHandler from 'utils/notifications';
import { Image } from 'utils/tip-tap-image-extension';
import { v4 as uuidv4 } from 'uuid';

import { NotificationMessageTemplateAutoCompleteValue } from '../../FlowBuilder/types';

interface FormValues {
  notificationMessageTemplateId: string;
  notificationType: 'Email' | 'SMS';
  description: string;
  subject: string;
  contents: string;
}

const recipientTemplateVariables: { [key: string]: string } = {
  'Recipient First Name': 'RECIPIENT_FIRST_NAME',
  'Recipient Last Name': 'RECIPIENT_LAST_NAME',
  'Recipient Email Address': 'RECIPIENT_EMAIL',
  'Recipient Phone Number': 'RECIPIENT_PHONE_NUMBER',
};

const gymTemplateVariables: { [key: string]: string } = {
  'Gym Name': 'GYM_NAME',
  'Gym Address': 'GYM_ADDRESS',
  'Gym Logo': 'GYM_LOGO',
  'Gym Email': 'GYM_EMAIL',
  [`Today's Date`]: `TODAYS_DATE`,
};

const DrawerEmailNotificationMessageTemplate = ({
  formId,
  opened,
  notificationMessageTemplateId,
  closeHandler,
  handleNotificationMessageTemplateChange,
  trigger,
  recipients,
  permission = true,
}: {
  formId: string;
  opened: boolean;
  notificationMessageTemplateId?: string | null;
  closeHandler: () => void;
  trigger?: Flow['trigger'] | null;
  recipients?: string | null;
  handleNotificationMessageTemplateChange?: (
    notificationMessageTemplate: NotificationMessageTemplateAutoCompleteValue,
  ) => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();

  const [dynamicTemplateManualTrigger, setDynamicTemplateManualTrigger] = useState<string | null>();

  const { data: dynamicTemplatesData, isSuccess: dynamicTemplatesIsSuccess } =
    useFlowDynamicTemplateVariables(trigger ? trigger : dynamicTemplateManualTrigger);

  const form = useForm<FormValues>({
    initialValues: {
      notificationMessageTemplateId: uuidv4(),
      notificationType: 'Email',
      description: '',
      subject: '',
      contents: '',
    },

    validate: {
      description: value =>
        !value || value.length > 255 ? 'Description must be between 1-255 characters.' : null,
      contents: value => (!value || value === '<p></p>' ? 'This field may not be blank.' : null),
      subject: value => (!value || value.length > 255 ? 'Subject must be between 1-255 characters' : null),
    },
  });

  const {
    data: notificationMessageTemplateData,
    isLoading: isNotificationMessageTemplateDataLoading,
    isFetching: isNotificationMessageTemplateDataFetching,
    isSuccess: isNotificationMessageTemplateDataSuccess,
  } = useNotificationMessageTemplate(notificationMessageTemplateId || '');
  const { data: gymData, isLoading: isGymLoading } = useGym(session?.gym?.gymId || null);

  const createMutation = useCreateNotificationMessageTemplate();
  const updateMutation = useUpdateNotificationMessageTemplate();

  const { successNotification, errorNotification } = notificationHandler();

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline as AnyExtension,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'document-gym-logo',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: form.getInputProps('contents').value,
    onUpdate: ({ editor: _editor }) => form.getInputProps('contents').onChange(_editor.getHTML()),
    editable: permission,
  });

  const staticTemplateVariables = useMemo(() => {
    if (recipients === 'Staff') {
      return gymTemplateVariables;
    }

    return {
      ...recipientTemplateVariables,
      ...gymTemplateVariables,
    };
  }, [recipients]);

  const templateVariables = useMemo(() => {
    if (dynamicTemplatesIsSuccess && dynamicTemplatesData) {
      const parsedDynamicTemplates = JSON.parse(dynamicTemplatesData.data);

      return {
        ...staticTemplateVariables,
        ...parsedDynamicTemplates,
      };
    }

    return {};
  }, [dynamicTemplatesData, dynamicTemplatesIsSuccess, staticTemplateVariables]);

  const handleSelectDynamicManualTemplate = (selectedTrigger: string): void => {
    setDynamicTemplateManualTrigger(selectedTrigger);
  };

  const [logoWidth, setLogoWidth] = useState(200);
  const [logoHeight, setLogoHeight] = useState(100);

  const updateImgAttributes = (attributeName: 'width' | 'height', attributeValue: number): void => {
    // Create a temporary DOM element to parse the HTML string
    const documentToParse = document.createElement('div');
    documentToParse.innerHTML = editor?.getHTML() || '';

    // Get all img elements in the temporary element
    const imgElements = documentToParse.querySelectorAll('img');

    // Loop through each img element and update the specified attribute
    imgElements.forEach(imgElement => {
      imgElement.setAttribute(attributeName, String(attributeValue));
    });

    // Get the updated HTML string
    const updatedHtmlText = documentToParse.innerHTML;

    // Update the editor and form with the modified HTML
    editor?.commands.setContent(updatedHtmlText);
    form.getInputProps('contents').onChange(updatedHtmlText);
  };

  const handleLogoWidthChange = (width: number): void => {
    setLogoWidth(width);
    updateImgAttributes('width', width);
  };

  const handleLogoHeightChange = (height: number): void => {
    setLogoHeight(height);
    updateImgAttributes('height', height);
  };

  const [isSubjectFocused, setIsSubjectFocused] = React.useState<boolean>(false);

  const handleTemplateVariableClick = (variable: string): void => {
    const templateToInsert = templateVariables[variable];
    if (isSubjectFocused) {
      if (templateToInsert !== 'GYM_LOGO') {
        const currentSubject = form.values.subject;
        form.setFieldValue('subject', currentSubject + templateToInsert + ' ');
      }
    } else {
      if (templateToInsert === 'GYM_LOGO') {
        if (!!gymData?.data.profilePicture) {
          editor
            ?.chain()
            .setImage({
              src: gymData?.data.profilePicture,
              width: String(logoWidth),
              height: String(logoHeight),
            })
            .focus()
            .run();
        } else {
          errorNotification("Gym Logo doesn't exist", 'This action is disabled until you upload gym logo.');
        }
      } else {
        editor?.chain().insertContent(`${templateToInsert} `).focus().run();
      }
    }
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
    editor?.commands.setContent('');
    setDynamicTemplateManualTrigger(null);
  };

  const handleSubmit = (values: FormValues): void => {
    if (notificationMessageTemplateId) {
      updateMutation.mutate(
        { ...values },
        {
          onSuccess: response => {
            successNotification(
              'NotificationMessageTemplateData template update success',
              `${response.data.description} successfully updated.`,
            );
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification(
              'NotificationMessageTemplateData template update failed',
              error.response?.data.message,
            );
          },
        },
      );
    } else if (session?.staff) {
      createMutation.mutate(
        {
          ...values,
          notificationMessageTemplateId: uuidv4(),
        },
        {
          onSuccess: response => {
            successNotification(
              'NotificationMessageTemplateData template creation success',
              `${response.data.description} successfully created.`,
            );
            const selectedNotificationMessageTemplate = {
              notificationMessageTemplateId: response.data.notificationMessageTemplateId
                ? response.data.notificationMessageTemplateId
                : '',
              label: response.data ? response.data.description : '',
              value: '',
            };
            if (handleNotificationMessageTemplateChange) {
              handleNotificationMessageTemplateChange(selectedNotificationMessageTemplate);
            }
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification(
              'NotificationMessageTemplateData template creation failed',
              error.response?.data.message,
            );
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (
      notificationMessageTemplateData?.data &&
      isNotificationMessageTemplateDataSuccess &&
      notificationMessageTemplateId &&
      opened
    ) {
      form.setFieldValue('contents', notificationMessageTemplateData.data.contents);
      form.setFieldValue('description', notificationMessageTemplateData.data.description);
      form.setFieldValue(
        'subject',
        notificationMessageTemplateData.data.subject ? notificationMessageTemplateData.data.subject : '',
      );
      form.setFieldValue(
        'notificationMessageTemplateId',
        notificationMessageTemplateData.data.notificationMessageTemplateId,
      );
      form.setFieldValue('notificationType', notificationMessageTemplateData.data.notificationType);
      editor?.commands.setContent(notificationMessageTemplateData.data.contents);

      if (notificationMessageTemplateData.data.contents.includes('img')) {
        // Create a temporary DOM element to parse the HTML string
        const documentToParse = document.createElement('div');
        documentToParse.innerHTML = notificationMessageTemplateData.data.contents;
        // Get all img elements in the temporary element
        const imgElement = documentToParse.querySelectorAll('img')[0];
        const initialWidthStr = imgElement.getAttribute('width');
        const initialHeightStr = imgElement.getAttribute('height');
        if (!!initialWidthStr) {
          setLogoWidth(Number(initialWidthStr));
        }
        if (!!initialHeightStr) {
          setLogoHeight(Number(initialHeightStr));
        }
      }
    }
  }, [notificationMessageTemplateData?.data, notificationMessageTemplateId, opened]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size={900}
      title={
        <Title order={3}>
          {notificationMessageTemplateId
            ? notificationMessageTemplateData?.data?.description
            : 'Create Email Notification Message Template'}
        </Title>
      }
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            disabled={
              !permission ||
              isNotificationMessageTemplateDataLoading ||
              isNotificationMessageTemplateDataFetching ||
              isGymLoading
            }
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay
        visible={
          isNotificationMessageTemplateDataLoading ||
          isNotificationMessageTemplateDataFetching ||
          isGymLoading
        }
      />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='md'>
          <TextInput label='Description' {...form.getInputProps('description')} disabled={!permission} />
          <Divider mx='-xl' />

          <FlowTriggerSelect
            label='Trigger'
            placeholder='Select a trigger'
            disabled={!!trigger}
            value={!!trigger ? trigger : dynamicTemplateManualTrigger}
            onChange={handleSelectDynamicManualTemplate}
          />

          {!trigger && !dynamicTemplateManualTrigger && (
            <Alert icon={<IconAlertCircle size='1rem' />} variant='filled'>
              Select a Trigger to show available dynamic template variables
            </Alert>
          )}

          <Stack spacing='xs'>
            <Group spacing='xs'>
              {Object.keys(templateVariables).map(variable => (
                <Button
                  key={variable}
                  radius='sm'
                  size='xs'
                  compact
                  onClick={() => handleTemplateVariableClick(variable)}
                  disabled={!permission}
                >
                  {variable}
                </Button>
              ))}
            </Group>
          </Stack>
          <TextInput
            label='Subject'
            {...form.getInputProps('subject')}
            disabled={!permission}
            onFocus={() => setIsSubjectFocused(true)}
          />
          {form.errors.contents && (
            <Alert icon={<IconAlertCircle size={theme.fontSizes.lg} />} color='red'>
              {form.errors.contents}
            </Alert>
          )}
          <RichTextEditor editor={editor} onFocus={() => setIsSubjectFocused(false)}>
            <RichTextEditor.Toolbar sticky display={permission ? 'default' : 'none'} stickyOffset={-24}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.H3 />
                <RichTextEditor.H4 />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignJustify />
                <RichTextEditor.AlignRight />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Hr />
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup sx={{ gap: theme.spacing.xs }}>
                <Group noWrap spacing='xs'>
                  <Input.Label size='xs'>Logo width:</Input.Label>
                  <NumberInput
                    value={logoWidth}
                    onChange={handleLogoWidthChange}
                    step={10}
                    min={0}
                    w={100}
                    size='xs'
                  />
                </Group>
                <Group noWrap spacing='xs'>
                  <Input.Label size='xs'>Logo height:</Input.Label>
                  <NumberInput
                    value={logoHeight}
                    onChange={handleLogoHeightChange}
                    step={10}
                    min={0}
                    size='xs'
                    w={100}
                  />
                </Group>
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
            <RichTextEditor.Content />
          </RichTextEditor>
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerEmailNotificationMessageTemplate;
